import React, { forwardRef, useState } from 'react';
import {
    TextInput as RNTextInput,
    View,
    Text,
    StyleSheet,
    TextInputProps as RNTextInputProps,
    ViewStyle,
    TextStyle,
    Pressable,
} from 'react-native';
import {useCurrentTheme} from "@/theming/useCurrentTheme";
import {ThemeType} from "@/theming/baseTheme";

export interface TextInputProps extends Omit<RNTextInputProps, 'style'> {
    label?: string;
    error?: string;
    helperText?: string;
    leftIcon?: React.ReactNode;
    rightIcon?: React.ReactNode;
    containerStyle?: ViewStyle;
    inputStyle?: TextStyle;
    variant?: 'outlined' | 'filled';
    size?: 'small' | 'medium' | 'large';
    disabled?: boolean;
    required?: boolean;
}

export const TextInput = forwardRef<RNTextInput, TextInputProps>(
    (
        {
            label,
            error,
            helperText,
            leftIcon,
            rightIcon,
            containerStyle,
            inputStyle,
            variant = 'outlined',
            size = 'medium',
            disabled = false,
            required = false,
            ...textInputProps
        },
        ref
    ) => {
        const {theme} = useCurrentTheme();
        const [isFocused, setIsFocused] = useState(false);
        const styles = createStyles(theme, variant, size, isFocused, !!error, disabled);

        const handleFocus = (e: any) => {
            setIsFocused(true);
            textInputProps.onFocus?.(e);
        };

        const handleBlur = (e: any) => {
            setIsFocused(false);
            textInputProps.onBlur?.(e);
        };

        const handleRightIconPress = () => {
            // Can be extended for common actions like clear, show/hide password, etc.
        };

        return (
            <View style={[styles.container, containerStyle]}>
                {label && (
                    <Text style={styles.label}>
                        {label}
                        {required && <Text style={styles.required}> *</Text>}
                    </Text>
                )}

                <View style={styles.inputContainer}>
                    {leftIcon && (
                        <View style={styles.leftIconContainer}>
                            {leftIcon}
                        </View>
                    )}

                    <RNTextInput
                        ref={ref}
                        style={[styles.input, inputStyle]}
                        placeholderTextColor={theme.colors.textSecondary}
                        editable={!disabled}
                        onFocus={handleFocus}
                        onBlur={handleBlur}
                        {...textInputProps}
                    />

                    {rightIcon && (
                        <Pressable
                            style={styles.rightIconContainer}
                            onPress={handleRightIconPress}
                            disabled={disabled}
                        >
                            {rightIcon}
                        </Pressable>
                    )}
                </View>

                {(error || helperText) && (
                    <Text style={error ? styles.errorText : styles.helperText}>
                        {error || helperText}
                    </Text>
                )}
            </View>
        );
    }
);

TextInput.displayName = 'TextInput';

const createStyles = (
    theme: ThemeType,
    variant: 'outlined' | 'filled',
    size: 'small' | 'medium' | 'large',
    isFocused: boolean,
    hasError: boolean,
    disabled: boolean
) => {
    const sizeConfig = {
        small: {
            height: 36,
            fontSize: theme.fontSize.sm,
            paddingHorizontal: theme.spacing.sm,
        },
        medium: {
            height: 44,
            fontSize: theme.fontSize.md,
            paddingHorizontal: theme.spacing.md,
        },
        large: {
            height: 52,
            fontSize: theme.fontSize.lg,
            paddingHorizontal: theme.spacing.lg,
        },
    };

    const currentSize = sizeConfig[size];

    const getBorderColor = () => {
        if (hasError) return theme.colors.error;
        if (isFocused) return theme.colors.primary;
        return theme.colors.border;
    };

    const getBackgroundColor = () => {
        if (disabled) return theme.colors.surface;
        if (variant === 'filled') return theme.colors.surface;
        return theme.colors.background;
    };

    return StyleSheet.create({
        container: {
            marginBottom: theme.spacing.sm,
        },
        label: {
            fontSize: theme.fontSize.sm,
            fontWeight: '500',
            color: theme.colors.text,
            marginBottom: theme.spacing.xs,
        },
        required: {
            color: theme.colors.error,
        },
        inputContainer: {
            flexDirection: 'row',
            alignItems: 'center',
            backgroundColor: getBackgroundColor(),
            borderWidth: variant === 'outlined' ? 1 : 0,
            borderColor: getBorderColor(),
            borderRadius: theme.borderRadius.md,
            minHeight: currentSize.height,
            paddingHorizontal: currentSize.paddingHorizontal,
            opacity: disabled ? 0.6 : 1,
            shadowColor: theme.colors.shadow,
            shadowOffset: {
                width: 0,
                height: 1,
            },
            shadowOpacity: 0.1,
            shadowRadius: 2,
            elevation: 1,
        },
        input: {
            flex: 1,
            fontSize: currentSize.fontSize,
            color: theme.colors.text,
            paddingVertical: theme.spacing.xs,
            textAlignVertical: 'center',
        },
        leftIconContainer: {
            marginRight: theme.spacing.sm,
            justifyContent: 'center',
            alignItems: 'center',
        },
        rightIconContainer: {
            marginLeft: theme.spacing.sm,
            justifyContent: 'center',
            alignItems: 'center',
            padding: theme.spacing.xs,
        },
        errorText: {
            fontSize: theme.fontSize.xs,
            color: theme.colors.error,
            marginTop: theme.spacing.xs,
        },
        helperText: {
            fontSize: theme.fontSize.xs,
            color: theme.colors.textSecondary,
            marginTop: theme.spacing.xs,
        },
    });
};
